import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio as RadioIcon, Mic, Heart, Disc3, ArrowUpRight, ExternalLink } from "lucide-react";
import UpcomingShows from "@/components/UpcomingShows";
import NewsletterSignup from "@/components/NewsletterSignup";
import FeaturedIn from "@/components/FeaturedIn";
import { TrustLine } from "@/components/TrustLine";
import SectionLabel from "@/components/SectionLabel";
import { HERO_NINA as HERO_IMG, AUDIO_PORN_COVER, OVA40_COVER, OVA40_KIT_URL, AUDIO_PORN_KIT_URL, OVA40_VINYL_URL, ELASTICSTAGE_URL } from "@/lib/assets";

const SPOTIFY_LISTEN = "https://open.spotify.com/album/2XDRxKesvCeCc6oS4dfX3g?si=O4VMDHl0T_K3rdG3-oHpfA";
const VINYL_IMG =
  "https://images.unsplash.com/photo-1649077549733-97815a45736b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB2aW55bCUyMHJlY29yZCUyMGRhcmt8ZW58MHx8fHwxNzc3NzI4NjQzfDA&ixlib=rb-4.1.0&q=85";
const VIBE_IMG =
  "https://images.unsplash.com/photo-1758637612400-09e155a1278c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdvbGQlMjBwdXJwbGUlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc3Mjg2NTh8MA&ixlib=rb-4.1.0&q=85";
const WELLNESS_IMG =
  "https://images.unsplash.com/photo-1762541115443-d83816da748c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHx3ZWxsbmVzcyUyMG1lZGl0YXRpb24lMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc3Mjg2NTh8MA&ixlib=rb-4.1.0&q=85";
const BIZ_IMG =
  "https://images.unsplash.com/photo-1770062421988-7929b4748e29?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxtdXNsaW0lMjBidXNpbmVzc3dvbWFuJTIwZGFyayUyMGx1eHVyeXxlbnwwfHx8fDE3Nzc3Mjg2NTh8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — Full brand banner */}
      <section className="relative bg-[#050308]">
        <h1 className="sr-only">
          Nina Capone™ — Philadelphia recording artist, live performer, media
          personality, radio host, entrepreneur, and keynote speaker. Respect
          The Name. Fear The Game.
        </h1>
        <img
          src={HERO_IMG}
          alt="Nina Capone — Recording Artist · Media Personality · Entrepreneur"
          className="w-full h-auto block select-none"
          fetchpriority="high"
        />
        {/* Fade the banner into the page */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[#050308]" />

        {/* Hero copy + CTAs */}
        <div className="relative bg-[#050308]">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 pb-16 pt-4 text-center fade-up">
            <h2 className="font-serif font-light tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
              Respect The Name.{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                Fear The Game.
              </em>
            </h2>
            <p className="mt-5 text-white/70 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
              <span className="text-white">
                Nina Capone
                <sup className="align-super text-[0.55em] tracking-normal opacity-80">™</sup>
              </span>{" "}
              is a Philadelphia recording artist, live performer, media
              personality, radio host, entrepreneur, and keynote speaker.
              Available for live musical performances, event hosting, media
              interviews, speaking engagements, podcast appearances, and select
              brand partnerships.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a
                href={SPOTIFY_LISTEN}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold inline-flex items-center gap-3 px-7 py-3 text-xs"
                data-testid="hero-listen-live"
              >
                <span className="inline-block w-2 h-2 bg-black rounded-full live-dot" />
                Listen to Nina Capone
              </a>
              <Link
                to="/epk"
                className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                data-testid="hero-book-nina"
              >
                Book Nina Capone
                <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/music"
                className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                data-testid="hero-explore-music"
              >
                Explore the Music
                <ArrowUpRight size={14} />
              </Link>
            </div>
            <TrustLine />
          </div>
        </div>
      </section>

      {/* QUICK STATS */}
      <section className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
          {[
            { k: "OUT NOW", l: "OVA 40 · Rap Album", highlight: true, href: "/music" },
            { k: "OUT NOW", l: "Audio Porn · R&B Album", highlight: true, href: "/music" },
            { k: "24/7", l: "Global Radio" },
            { k: "1", l: "Mission — Empower Da Streets" },
          ].map((s) => (
            <div key={s.l} className="px-4 py-10">
              {s.href ? (
                <Link
                  to={s.href}
                  data-testid="home-album-stat"
                  className="block group"
                >
                  <div
                    className={`font-serif tracking-tighter ${
                      s.highlight
                        ? "text-2xl lg:text-3xl flex items-center gap-2"
                        : "text-5xl lg:text-6xl"
                    } group-hover:opacity-80 transition-opacity`}
                    style={{ color: "var(--gold)" }}
                  >
                    {s.highlight && (
                      <span
                        className="inline-block w-2 h-2 rounded-full live-dot"
                        style={{ background: "var(--gold)" }}
                      />
                    )}
                    {s.k}
                  </div>
                  <div className="text-xs uppercase tracking-[0.25em] text-white/50 mt-2 group-hover:text-white/75 transition-colors">
                    {s.l}
                  </div>
                </Link>
              ) : (
                <>
                  <div
                    className={`font-serif tracking-tighter ${
                      s.highlight
                        ? "text-2xl lg:text-3xl flex items-center gap-2"
                        : "text-5xl lg:text-6xl"
                    }`}
                    style={{ color: "var(--gold)" }}
                  >
                    {s.highlight && (
                      <span
                        className="inline-block w-2 h-2 rounded-full live-dot"
                        style={{ background: "var(--gold)" }}
                      />
                    )}
                    {s.k}
                  </div>
                  <div className="text-xs uppercase tracking-[0.25em] text-white/50 mt-2">
                    {s.l}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* AS FEATURED IN */}
      <FeaturedIn />

      {/* BOTH ALBUMS — OUT NOW */}
      <section
        className="relative border-t border-white/5 overflow-hidden py-20"
        data-testid="home-albums-out-now"
      >
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${AUDIO_PORN_COVER})`,
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
            filter: "blur(48px) saturate(1.1)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,3,8,0.9) 0%, rgba(10,5,15,0.96) 100%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span
              className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.3em] border"
              style={{ color: "var(--gold)", borderColor: "rgba(212,175,55,0.5)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full live-dot" style={{ background: "var(--gold)" }} />
              Both Albums · Out Now
            </span>
            <h2 className="mt-5 font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">
              Two albums.{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                One era.
              </em>
            </h2>
            <p className="mt-4 text-white/60 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
              Stream them everywhere, or own the limited collector kits — vinyl,
              CDs, and exclusive physical extras.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                cover: OVA40_COVER,
                name: "OVA 40",
                genre: "Rap Album",
                kit: OVA40_KIT_URL,
                vinyl: OVA40_VINYL_URL,
                testid: "home-album-ova40",
              },
              {
                cover: AUDIO_PORN_COVER,
                name: "Audio Porn",
                genre: "R&B Album",
                kit: AUDIO_PORN_KIT_URL,
                vinyl: ELASTICSTAGE_URL,
                testid: "home-album-audioporn",
              },
            ].map((a) => (
              <div key={a.name} className="surface p-5 flex flex-col sm:flex-row gap-5" data-testid={a.testid}>
                <Link to="/music" className="shrink-0 mx-auto sm:mx-0 group">
                  <div className="relative w-40 h-40 rounded-md overflow-hidden ring-1 ring-white/10">
                    <img
                      src={a.cover}
                      alt={`${a.name} album cover`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </Link>
                <div className="flex-1 flex flex-col justify-center text-center sm:text-left">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/45">{a.genre}</p>
                  <h3 className="font-serif text-3xl tracking-tighter font-light mt-1">{a.name}</h3>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-5">
                    <Link
                      to="/music"
                      className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-[11px]"
                      data-testid={`${a.testid}-stream`}
                    >
                      <Disc3 size={13} /> Stream Now
                    </Link>
                    <a
                      href={a.kit}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost-gold inline-flex items-center gap-2 px-5 py-2.5 text-[11px]"
                      data-testid={`${a.testid}-kit`}
                    >
                      Collector Kit <ArrowUpRight size={13} />
                    </a>
                    <a
                      href={a.vinyl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost-gold inline-flex items-center gap-2 px-5 py-2.5 text-[11px]"
                      data-testid={`${a.testid}-vinyl-cd`}
                    >
                      Vinyl &amp; CDs <ArrowUpRight size={13} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLAR LINKS */}
      <section className="py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <SectionLabel number="01" label="The Empire" />
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light">
                Four channels.
                <br />
                <em className="italic" style={{ color: "var(--gold)" }}>
                  One frequency.
                </em>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-white/60 leading-relaxed">
                Nina built her empire the long way: brick, bar, and broadcast.
                What lives here is the sum of three decades — radio, records,
                philanthropy, and the boardroom.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <PillarCard
              to="/radio"
              testId="pillar-radio"
              icon={<RadioIcon size={20} />}
              eyebrow="01 · Media"
              title="In Da Streets Radio"
              copy="24/7 global station, Respect The Hustle podcast, and an open submission door for independent artists."
              image={HERO_IMG}
              big
            />
            <PillarCard
              to="/music"
              testId="pillar-music"
              icon={<Disc3 size={20} />}
              eyebrow="02 · Catalog"
              title="OVA 40 + Vibes"
              copy="The 17-track debut, with curated cuts across The Flow, The Vibe, and The Spirit."
              image={VINYL_IMG}
            />
            <PillarCard
              to="/inspire"
              testId="pillar-inspire"
              icon={<Heart size={20} />}
              eyebrow="03 · Mission"
              title="Inspire Da Streets"
              copy="Mental health, tech training, and youth empowerment — searchable wellness blog inside."
              image={WELLNESS_IMG}
            />
            <PillarCard
              to="/epk"
              testId="pillar-epk"
              icon={<Mic size={20} />}
              eyebrow="04 · Business"
              title="EPK · My Biz Consulting"
              copy="Press kit, AI tech consulting, and booking forms for performances + keynote speaking."
              image={BIZ_IMG}
              big
            />
          </div>
        </div>
      </section>

      {/* UPCOMING SHOWS */}
      <UpcomingShows
        heading="Upcoming Shows & Appearances"
        showLabel="Live · On The Calendar"
        limit={6}
        hideWhenEmpty
      />

      {/* NEWSLETTER SIGNUP — INNER CIRCLE */}
      <NewsletterSignup source="home-mid" testId="home-newsletter-signup" />

      {/* QUOTE / EDITORIAL */}
      <section className="py-32 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${VIBE_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "saturate(0.7)",
          }}
        />
        <div className="absolute inset-0 bg-[#050308]/85" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <p className="eyebrow mb-6">Mission Statement</p>
          <blockquote className="font-serif text-3xl sm:text-4xl lg:text-6xl tracking-tighter leading-[1.05] font-light">
            "I built every door I walked through.
            <br />
            <em
              className="italic font-light"
              style={{ color: "var(--gold)" }}
            >
              Now I'm leaving them open."
            </em>
          </blockquote>
          <div className="text-xs tracking-[0.3em] text-white/40 mt-10 uppercase">
            — Nina Capone
          </div>
        </div>
      </section>
    </div>
  );
}

function PillarCard({ to, icon, eyebrow, title, copy, image, big, testId }) {
  return (
    <Link
      to={to}
      className={`surface group block relative overflow-hidden ${
        big ? "lg:col-span-7" : "lg:col-span-5"
      } min-h-[340px]`}
      data-testid={testId}
    >
      <div
        className="absolute inset-0 opacity-50 group-hover:opacity-70 transition-opacity duration-700"
        style={{
          backgroundImage: `url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(5,3,8,0.4) 0%, rgba(5,3,8,0.95) 95%)",
        }}
      />
      <div className="relative h-full flex flex-col justify-between p-8 lg:p-10">
        <div className="flex items-center justify-between">
          <p className="eyebrow">{eyebrow}</p>
          <span style={{ color: "var(--gold)" }}>{icon}</span>
        </div>
        <div className="space-y-4">
          <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light">
            {title}
          </h3>
          <p className="text-sm text-white/60 max-w-md leading-relaxed">
            {copy}
          </p>
          <div
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] pt-2"
            style={{ color: "var(--gold)" }}
          >
            Enter <ArrowUpRight size={14} />
          </div>
        </div>
      </div>
    </Link>
  );
}

